<script setup lang="ts">
import {onMounted, onUnmounted, ref} from 'vue';
import {AUTH_LOG_LABELS, type AuthLogRegel} from 'common';
import {authLog, verwijderAuthLog} from '@/base/callBackend';
import {renderDateTimeLocal} from '@/base/utils';
import CardPanel from '@/views/ui/CardPanel.vue';
import RowGrid from '@/views/ui/RowGrid.vue';
import IconButton from '@/views/ui/IconButton.vue';
import StatusTag from '@/views/ui/StatusTag.vue';
import {ChevronDown, ChevronRight, Trash2} from 'lucide-vue-next';

// polled: a row can be caused by another user or browser (a login, a forgotten password, a reset link), not only by this one
const VERVERS_MS: number  = 5_000;
const URL_REGEX: RegExp   = /(https?:\/\/[^\s]+)/;
const LOG_COLUMNS: string = '2rem 9rem 8rem 15rem 8rem minmax(8rem, 1fr) 2rem';

const regels = ref<AuthLogRegel[]>([]);
const open   = ref<Set<string>>(new Set());
let timer: ReturnType<typeof setInterval> | undefined;

onMounted(async () => {
    await laad();
    timer = setInterval(laad, VERVERS_MS);
});

onUnmounted(() => {
    clearInterval(timer);
});

async function laad() {
    try {
        regels.value = await authLog();
    } catch (error) {
        // the next poll tries again
    }
}

async function verwijder(regel: AuthLogRegel) {
    await verwijderAuthLog(regel.id);
    regels.value = regels.value.filter(r => r.id !== regel.id);
}

function toggle(regel: AuthLogRegel) {
    if (open.value.has(regel.id)) {
        open.value.delete(regel.id);
    } else {
        open.value.add(regel.id);
    }
}

function isMail(regel: AuthLogRegel): boolean {
    return regel.soort === 'welkom' || regel.soort === 'reset' || regel.soort === 'code';
}

function status(regel: AuthLogRegel): string {
    if (!isMail(regel)) {
        return regel.gelukt ? 'ok' : 'geweigerd';
    }
    if (regel.gelukt) {
        return 'verstuurd';
    }
    return regel.fout ? `mislukt: ${regel.fout}` : 'niet verstuurd (geen mail op deze site)';
}

function statusKind(regel: AuthLogRegel): 'ok' | 'warn' | 'bad' | 'info' {
    if (regel.gelukt) {
        return 'ok';
    }
    return regel.fout || !isMail(regel) ? 'bad' : 'info';
}

// the text is server-generated (see maakMail), so its links are the only thing to pick out; no html rendering
function delen(tekst: string): string[] {
    return tekst.split(URL_REGEX).filter(d => d.length > 0);
}

function isUrl(deel: string): boolean {
    return URL_REGEX.test(deel);
}
</script>

<template>
    <CardPanel title="Logboek" :count="regels.length ? `${regels.length}` : ''" class="mt-6" min-width="58rem" data-testid="auth-log">
        <div v-if="regels.length === 0" class="px-4 py-3 text-xs text-zzc-muted">
            nog niets gelogd
        </div>
        <template v-else>
            <RowGrid :columns="LOG_COLUMNS" header>
                <span/>
                <span>tijd</span>
                <span>wat</span>
                <span>gebruiker</span>
                <span>ip</span>
                <span>status</span>
                <span/>
            </RowGrid>
            <template v-for="regel in regels" :key="regel.id">
                <RowGrid :columns="LOG_COLUMNS" data-testid="auth-log-regel">
                    <IconButton v-if="regel.tekst" :icon="open.has(regel.id) ? ChevronDown : ChevronRight" :label="open.has(regel.id) ? 'tekst verbergen' : 'tekst tonen'" size="sm" @click="toggle(regel)"/>
                    <span v-else/>
                    <span class="whitespace-nowrap">{{ renderDateTimeLocal(new Date(regel.tijd)) }}</span>
                    <span class="truncate">{{ AUTH_LOG_LABELS[regel.soort] }}</span>
                    <span class="truncate">{{ regel.door ? regel.door + ' -> ' + regel.aan : (regel.aan ?? '-') }}</span>
                    <span class="truncate">{{ regel.ip ?? '-' }}</span>
                    <span><StatusTag :kind="statusKind(regel)">{{ status(regel) }}</StatusTag></span>
                    <IconButton :icon="Trash2" label="regel verwijderen" variant="danger" size="sm" @click="verwijder(regel)"/>
                </RowGrid>
                <div v-if="regel.tekst && open.has(regel.id)"
                     class="mx-4 mb-3 p-3 rounded-control bg-zzc-lichtgrijs whitespace-pre-line text-sm"
                     data-testid="auth-log-tekst"
                >
                    <template v-for="(deel, i) in delen(regel.tekst)" :key="i">
                        <a v-if="isUrl(deel)" :href="deel" target="_blank" class="text-zzc-blauw underline break-all">{{ deel }}</a>
                        <span v-else>{{ deel }}</span>
                    </template>
                </div>
            </template>
        </template>
    </CardPanel>
</template>
